import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGenerateFacturePdf,
  type FactureRecouvrementRow,
} from '@/hooks/queries/useRecouvrementEntreprises';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import type { ClientB2B, EntrepriseEmettrice } from '@/lib/pdf-facture-algerie';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  facture: FactureRecouvrementRow | null;
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

export function GeneratePdfFactureDialog({ open, onOpenChange, facture }: Props): JSX.Element {
  const generate = useGenerateFacturePdf();
  const { data: entreprises = [] } = useEntreprises();
  const { data: tiers = [] } = useTiers({
    entrepriseId: facture?.entreprise_id ?? null,
    type: 'client_b2b',
    search: '',
  });

  const [dateEmission, setDateEmission] = useState(new Date().toISOString().slice(0, 10));
  const [dateEcheance, setDateEcheance] = useState('');
  const [type, setType] = useState<'proforma' | 'definitive'>('definitive');
  const [includeCgv, setIncludeCgv] = useState(true);

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    setDateEmission(today.toISOString().slice(0, 10));
    setDateEcheance(addDays(today, 30).toISOString().slice(0, 10));
    setType('definitive');
    setIncludeCgv(true);
  }, [open]);

  if (!facture)
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );

  const entrepriseEmettrice = entreprises.find((e) => e.id === facture.entreprise_id);
  const clientTier = tiers.find((t) => t.id === facture.tier_id);

  // Sprint 6.9.3.1 — validation données légales pour facture définitive (institutionnelle).
  const emettriceNif = (entrepriseEmettrice as unknown as { nif?: string | null } | undefined)?.nif;
  const clientNif = (clientTier as unknown as { nif?: string | null } | undefined)?.nif;
  const isDefinitive = type === 'definitive';
  const missingEmettriceNif = isDefinitive && !emettriceNif?.trim();
  const missingClientNif = isDefinitive && !clientNif?.trim();
  const blocked = missingEmettriceNif || missingClientNif;

  const handleGenerate = async (): Promise<void> => {
    if (!entrepriseEmettrice) {
      toast.error('Entreprise émettrice introuvable');
      return;
    }
    if (blocked) {
      toast.error(
        'NIF manquant — obligatoire pour une facture définitive. Passez en proforma ou complétez la fiche entreprise.',
      );
      return;
    }
    const emettrice: EntrepriseEmettrice = {
      nom: entrepriseEmettrice.nom,
      adresse: (entrepriseEmettrice as unknown as { adresse?: string | null }).adresse ?? null,
      telephone:
        (entrepriseEmettrice as unknown as { telephone?: string | null }).telephone ?? null,
      email: (entrepriseEmettrice as unknown as { email?: string | null }).email ?? null,
      nif: (entrepriseEmettrice as unknown as { nif?: string | null }).nif ?? null,
      nis: (entrepriseEmettrice as unknown as { nis?: string | null }).nis ?? null,
      rc: (entrepriseEmettrice as unknown as { rc?: string | null }).rc ?? null,
      ai: (entrepriseEmettrice as unknown as { ai?: string | null }).ai ?? null,
      rib: (entrepriseEmettrice as unknown as { rib?: string | null }).rib ?? null,
    };
    const client: ClientB2B = {
      nom: facture.entreprise_cliente_nom,
      adresse: (clientTier as unknown as { adresse?: string | null } | undefined)?.adresse ?? null,
      nif: (clientTier as unknown as { nif?: string | null } | undefined)?.nif ?? null,
      nis: (clientTier as unknown as { nis?: string | null } | undefined)?.nis ?? null,
      rc: (clientTier as unknown as { rc?: string | null } | undefined)?.rc ?? null,
    };

    try {
      const result = await generate.mutateAsync({
        facture,
        dateEmission,
        dateEcheance,
        type,
        includeCgv,
        emettrice,
        client,
      });
      // Download immédiat
      const a = document.createElement('a');
      a.href = result.signedUrl;
      a.download = `${facture.reference_facture}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Facture ${facture.reference_facture}.pdf générée et téléchargée`);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Générer la facture PDF · {facture.reference_facture}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 text-xs text-blue-900">
            Format conforme aux usages Algérie : NIF / NIS / RC / TVA 19 % / Net à payer en lettres.
            Le PDF est uploadé dans le bucket privé <code>factures/</code> et téléchargé localement.
          </div>

          <Field label="Type de facture">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType('proforma')}
                className={cn(
                  'rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all',
                  type === 'proforma'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                )}
              >
                Proforma
              </button>
              <button
                type="button"
                onClick={() => setType('definitive')}
                className={cn(
                  'rounded-lg border-2 px-3 py-2 text-xs font-semibold transition-all',
                  type === 'definitive'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                )}
              >
                Définitive
              </button>
            </div>
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date d'émission" required>
              <input
                type="date"
                value={dateEmission}
                onChange={(e) => setDateEmission(e.target.value)}
                className="input"
                required
              />
            </Field>
            <Field label="Date d'échéance" required hint="Auto +30 j">
              <input
                type="date"
                value={dateEcheance}
                onChange={(e) => setDateEcheance(e.target.value)}
                className="input"
                required
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs">
            <input
              type="checkbox"
              checked={includeCgv}
              onChange={(e) => setIncludeCgv(e.target.checked)}
              className="h-4 w-4"
            />
            <span>
              <strong>Inclure les CGV</strong> (page 2 — recommandé pour facture définitive)
            </span>
          </label>

          {!entrepriseEmettrice && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              ⚠ Entreprise émettrice introuvable. Vérifiez que la facture est bien rattachée à une
              entreprise dans <code>/parametres/entreprises</code>.
            </div>
          )}

          {/* Sprint 6.9.3.1 — Validation NIF pour facture définitive */}
          {blocked && (
            <div className="rounded-md border-2 border-rose-300 bg-rose-50 px-3 py-2.5 text-xs text-rose-800">
              <div className="flex items-start gap-2">
                <span className="text-base leading-none">🚫</span>
                <div className="flex-1">
                  <div className="font-bold">Génération bloquée — NIF manquant</div>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">
                    {missingEmettriceNif && (
                      <li>NIF entreprise émettrice manquant ({entrepriseEmettrice?.nom ?? '—'})</li>
                    )}
                    {missingClientNif && (
                      <li>NIF client manquant ({facture.entreprise_cliente_nom})</li>
                    )}
                  </ul>
                  <div className="mt-2 text-[11px] text-rose-700">
                    Solutions : (1) basculer en <strong>proforma</strong> ci-dessus, (2) compléter
                    le NIF dans <code>/parametres/tiers</code> ou{' '}
                    <code>/parametres/entreprises</code>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {!blocked && entrepriseEmettrice && (!emettriceNif || !clientNif) && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              ℹ Mentions légales partielles (proforma OK). Renseignez NIF / NIS / RC dans les fiches
              pour qu&apos;ils apparaissent sur la facture définitive.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={generate.isPending || !entrepriseEmettrice || blocked}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {generate.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Générer + Télécharger
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-0.5 block text-[10px] text-slate-400">{hint}</span>}
    </label>
  );
}
